import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { useAuth } from "@/hooks/use-auth";
import { doc, setDoc, updateDoc, deleteDoc, collection, getDocs, serverTimestamp, query, orderBy } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { maybeShowInterstitial } from "@/lib/ads";

import {
  getUserSettings,
  updateUserSettings,
  isAccessTokenValid,
  refreshGoogleToken,
  requestDriveAccess as requestDriveAccessFromFirebase,
  getBusinessProfile,
  getBusinessVerification,
  saveBusinessProfile as saveBusinessProfileToFirestore,
  clearBusinessProfileLogo,
  getNextGstInvoiceNumber,
  publishInvoiceToFirestore,
  deletePublicInvoice,
  saveRewardEvent,
  loadRewardHistory,
  atomicUpdateRewardPoints,
  subscribeToUserSettings,
  getTokenForNotifications,
  db as firebaseDb,
  functions,
  functionsAsia,
  type UserMode,
  type BusinessProfile,
  type RewardEvent,
  getPendingReferralRewards,
  markReferralRewarded,
  updateReferralStep,
} from "@/lib/firebase";
import { generateAppBarcode } from "@/lib/barcodeUtils";
import { db as localDb } from "@/lib/db";
import {
  getProducts,
  addProduct,
  updateProduct,
  deleteProduct,
  getTransactions,
  addTransactions,
  deleteTransaction as deleteTransactionFromSheet,
  calculateDashboardStats,
  calculateWeeklyChartData,
  listUserSpreadsheets,
  createSpreadsheet,
  updateProductStock,
  type Product,
  type Transaction,
  type DashboardStats,
  type ChartDataPoint,
} from "@/lib/googleSheetsService";

import { toast as toastify } from "react-toastify";
import { toast } from "@/hooks/use-toast";

async function syncPendingTransactions(spreadsheetId: string) {
  try {
    const pending = await localDb.transactions
      .where("syncStatus")
      .equals("pending")
      .toArray();
    if (pending.length === 0) return;

    const transactionsByOrder = pending.reduce(
      (acc, curr) => {
        const orderKey =
          curr.fullOrderId || curr.id.split("_").slice(0, -1).join("_");
        if (!acc[orderKey]) acc[orderKey] = [];
        acc[orderKey].push(curr);
        return acc;
      },
      {} as Record<string, typeof pending>,
    );

    for (const [orderId, items] of Object.entries(transactionsByOrder)) {
      try {
        await addTransactions(
          spreadsheetId,
          items.map((i) => ({
            productId: i.productId,
            productName: i.productName,
            quantity: i.quantity,
            rate: i.rate,
          })),
          items[0].type,
          orderId,
        );

        const itemIds = items.map((i) => i.id);
        await localDb.transactions
          .where("id")
          .anyOf(itemIds)
          .modify({ syncStatus: "synced" });
      } catch (err) {
        console.error(`Failed to sync transactions in background:`, err);
        const itemIds = items.map((i) => i.id);
        await localDb.transactions
          .where("id")
          .anyOf(itemIds)
          .modify({ syncStatus: "failed" });
      }
    }
  } catch (err) {
    console.error("Sync process error:", err);
  }
}

async function syncPendingProducts(spreadsheetId: string) {
  try {
    const pending = await localDb.products
      .where("syncStatus")
      .equals("pending")
      .toArray();
    if (pending.length === 0) return;

    for (const product of pending) {
      try {
        try {
          await updateProduct(spreadsheetId, product as any);
        } catch (updateErr: any) {
          await addProduct(spreadsheetId, product as any);
        }
        await localDb.products.update(product.id, { syncStatus: "synced" });
      } catch (err) {
        console.error(`Failed to sync product ${product.id}:`, err);
        await localDb.products.update(product.id, { syncStatus: "failed" });
      }
    }
  } catch (err) {
    console.error("Product sync process error:", err);
  }
}

async function syncPendingOps(spreadsheetId: string) {
  try {
    const ops = await localDb.pendingOps.toArray();
    if (ops.length === 0) return;

    for (const op of ops) {
      try {
        if (op.opType === "delete_product") {
          await deleteProduct(spreadsheetId, op.targetId);
        } else if (op.opType === "delete_transaction") {
          await deleteTransactionFromSheet(spreadsheetId, op.targetId);
        }
        await localDb.pendingOps.delete(op.id!);
      } catch (err: any) {
        if (
          err.message?.includes("401") ||
          err.message?.includes("expired") ||
          err.message?.includes("session")
        ) {
          console.log(`Pending op ${op.opType} deferred (auth):`, op.targetId);
        } else {
          console.error(`Pending op ${op.opType} failed, removing:`, err);
          await localDb.pendingOps.delete(op.id!);
        }
      }
    }
  } catch (err) {
    console.error("Pending ops sync error:", err);
  }
}

interface DataContextType {
  isProUpgradeModalOpen: boolean;
  openProUpgradeModal: () => void;
  closeProUpgradeModal: () => void;
  isOnline: boolean;
  spreadsheetId: string | null;
  isSheetConnected: boolean;
  isLoading: boolean;
  dataInitialized: boolean;
  needsTokenRefresh: boolean;
  userMode: UserMode;
  isPaid: boolean;
  rewardPoints: number;
  rewardHistory: RewardEvent[];
  proModeExpiry: string | null;
  businessName: string;
  businessLogo: string | null;
  profileCompleted: boolean;
  businessProfile: BusinessProfile | null;
  // Live owner-device verified-business flag. Anti-tamper: true only when the
  // admin-written verifiedName still matches the current businessName.
  businessVerified: boolean;
  products: Product[];
  transactions: Transaction[];
  dashboardStats: DashboardStats | null;
  chartData: ChartDataPoint[];
  reports: any[];
  saveReportRecord: (metadata: any) => Promise<void>;
  refreshProducts: () => Promise<void>;
  refreshTransactions: () => Promise<void>;
  refreshAll: () => Promise<void>;
  refreshToken: () => Promise<void>;
  createProduct: (
    product: Omit<Product, "id"> & { isFromExternal?: boolean },
  ) => Promise<Product>;
  editProduct: (product: Product) => Promise<Product>;
  removeProduct: (productId: string) => Promise<void>;
  createTransactions: (
    items: {
      productId: string;
      productName: string;
      quantity: number;
      rate: number;
    }[],
    type: "sale" | "expense",
    generateInvoice?: boolean,
    isGstBill?: boolean,
  ) => Promise<Transaction[]>;
  createStockExpense: (item: {
    productId: string;
    productName: string;
    quantity: number;
    rate: number;
  }) => Promise<void>;
  deleteTransaction: (transactionId: string) => Promise<void>;
  publishInvoice: (orderId: string) => Promise<string | null>;
  convertToGstBill: (transactionIds: string[]) => Promise<string | null>;
  connectSpreadsheet: (spreadsheetId: string) => Promise<void>;
  disconnectSpreadsheet: () => Promise<void>;
  createNewSpreadsheet: () => Promise<void>;
  listSpreadsheets: () => Promise<any[]>;
  setUserMode: (mode: UserMode) => Promise<void>;
  updateUserMode: (mode: "pro" | "normal") => Promise<void>;
  updateRewardPoints: (
    points: number,
    description?: string,
    action?: string,
  ) => Promise<void>;
  upgradeToProWithPoints: (plan: {
    points: number;
    days: number;
  }) => Promise<boolean>;
  redeemPhysicalReward: (rewardId: string, rewardName: string, points: number, shippingDetails?: any) => Promise<boolean>;
  checkProExpiry: () => void;
  proExpired: boolean;
  dismissProExpiry: (switchToNormal: boolean) => Promise<void>;
  setBusinessName: (name: string) => Promise<void>;
  saveBusinessProfile: (profile: BusinessProfile) => Promise<void>;
  updateBusinessLogo: (logo: string | null) => Promise<void>;
  requestDriveAccess: () => Promise<void>;
  settingsLoaded: boolean;
  error: string | null;
  readNotificationIds: string[];
  updateNotificationReadIds: (ids: string[]) => Promise<void>;
  dismissedNotificationIds: string[];
  dismissNotificationIds: (ids: string[]) => Promise<void>;
  hideFavoriteProducts: boolean;
  setHideFavoriteProducts: (value: boolean) => Promise<void>;
  hideSalesSummary: boolean;
  setHideSalesSummary: (value: boolean) => Promise<void>;
  printerPaperWidth: "58" | "80" | "A4";
  setPrinterPaperWidth: (value: "58" | "80" | "A4") => Promise<void>;
  printerPrintLogo: boolean;
  setPrinterPrintLogo: (value: boolean) => Promise<void>;
  deletionRequestedAt: string | null;
  deletionReason: string;
  requestAccountDeletion: (reason: string) => Promise<void>;
  revokeAccountDeletion: () => Promise<void>;
  invoiceFooter: string;
  setInvoiceFooter: (v: string) => Promise<void>;
  invoiceTerms: string;
  setInvoiceTerms: (v: string) => Promise<void>;
  invoiceLayout: 'standard' | 'basic';
  setInvoiceLayout: (v: 'standard' | 'basic') => Promise<void>;
  printQrCode: boolean;
  setPrintQrCode: (v: boolean) => Promise<void>;

  // Legacy-compat derived from plan.billing (read-only)
  subscriptionId: string | null;
  subscriptionStatus: 'active' | 'paused' | 'cancelled' | 'past_due' | 'created' | 'completed' | null;
  subscriptionPlan: string | null;
  subscriptionCurrentPeriodEnd: string | null;

  // Canonical subscription
  plan: import("@/lib/planTypes").Plan | null;
  trialsUsed: import("@/lib/planTypes").TrialsUsed | null;
  devices: import("@/lib/planTypes").DevicesState | null;
  // Ultimate owner mobile = active Ultimate tier on this device. Used to gate
  // the read-only admin-console UI vs full POS routes.
  isUltimateOwnerMobile: boolean;

  // AI SETTINGS (public flags only — raw keys in private subcollection, Admin SDK reads server-side)
  aiSettings: {
    customKeyProvider?: "gemini" | "openai" | null;
    hasCustomGeminiKey?: boolean;
    hasCustomOpenaiKey?: boolean;
    adminImagesUsedToday?: number;
    adminTaxonomyUsedToday?: number;
    lastImageDate?: string;
  } | null;

  // 🏷️ LABEL PRINTER FIELDS
  labelPrinterLanguage: "escpos" | "tspl" | "zpl";
  setLabelPrinterLanguage: (v: "escpos" | "tspl" | "zpl") => Promise<void>;
  labelWidth: number;
  labelHeight: number;
  setLabelSize: (width: number, height: number) => Promise<void>;
}

const DataContext = createContext<DataContextType | null>(null);

const deductedOrderIds = new Set<string>();

export function DataProvider({ children }: { children: ReactNode }) {
  const updateUserMode = async (mode: "pro" | "normal") => {
    if (!user) return;
    try {
      const userRef = doc(firebaseDb, "users", user.uid);
      await updateDoc(userRef, { userMode: mode });
    } catch (error) {
      console.error("Error updating user mode:", error);
    }
  };

  const [isProUpgradeModalOpen, setIsProUpgradeModalOpen] = useState(false);
  const openProUpgradeModal = () => setIsProUpgradeModalOpen(true);
  const closeProUpgradeModal = () => setIsProUpgradeModalOpen(false);
  const { user } = useAuth();
  const [isOnline, setIsOnline] = useState(true);
  const [spreadsheetId, setSpreadsheetId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsTokenRefresh, setNeedsTokenRefresh] = useState(false);
  const [rewardPoints, setRewardPoints] = useState(0);
  const [rewardHistory, setRewardHistory] = useState<RewardEvent[]>([]);
  const [readNotificationIds, setReadNotificationIds] = useState<string[]>([]);
  const [dismissedNotificationIds, setDismissedNotificationIds] = useState<string[]>([]);
  const [hideFavoriteProducts, setHideFavoriteProductsState] = useState<boolean>(false);
  const [hideSalesSummary, setHideSalesSummaryState] = useState<boolean>(false);
  const [printerPaperWidth, setPrinterPaperWidthState] = useState<"58" | "80" | "A4">("58");
  const [printerPrintLogo, setPrinterPrintLogoState] = useState<boolean>(true);
  const [deletionRequestedAt, setDeletionRequestedAt] = useState<string | null>(null);
  const [deletionReason, setDeletionReason] = useState<string>("");
  const [plan, setPlan] = useState<import("@/lib/planTypes").Plan | null>(null);
  const [trialsUsed, setTrialsUsed] = useState<import("@/lib/planTypes").TrialsUsed | null>(null);
  const [devices, setDevices] = useState<import("@/lib/planTypes").DevicesState | null>(null);
  const [aiSettings, setAiSettings] = useState<DataContextType["aiSettings"]>(null);
  const [proExpired, setProExpired] = useState(false);

  // Derived legacy-compat values from `plan` — preserves consumer API.
  // Ultimate users see the Pro UI by default (superset of Pro features).
  // Treat Ultimate as "pro" for userMode so all `userMode === "pro"` gates
  // in legacy code (Dashboard, Products, Transactions, Alerts, etc.) work.
  const [uiMode, setUiModeState] = useState<"pro" | "ultimate" | "normal" | null>(null);
  // Normal mode removed (2026): every user gets one unified UI, so `userMode` is always "pro".
  // The free-vs-paid difference is ads (see `isPaid`), not features. `settings.uiMode` is no longer read.
  const userMode: UserMode = "pro";
  // `isPaid` = active paid subscriber (pro OR ultimate). Drives ad-gating only (see useShowAds).
  const isPaid = useMemo(
    () => !!plan && plan.tier !== "basic" && !!plan.expiresAt && new Date(plan.expiresAt).getTime() > Date.now(),
    [plan],
  );
  const isUltimateOwnerMobile = useMemo(
    () => plan?.tier === "ultimate" && !!plan?.expiresAt && new Date(plan.expiresAt) > new Date(),
    [plan]
  );
  const proModeExpiry = useMemo(
    () => (plan?.tier === "pro" || plan?.tier === "ultimate" ? plan?.expiresAt || null : null),
    [plan]
  );
  const [businessName, setBusinessNameState] = useState("");
  const [businessLogo, setBusinessLogo] = useState<string | null>(null);
  const [profileCompleted, setProfileCompleted] = useState(false);
  const [businessProfile, setBusinessProfile] = useState<BusinessProfile | null>(null);
  const [businessVerification, setBusinessVerification] = useState<{ verified: boolean; verifiedName: string } | null>(null);
  // Anti-tamper (mirrors native-Android verifiedFor): the DAILYHISAAB VERIFIED badge shows ONLY when
  // the admin-written verifiedName still matches the CURRENT businessName. DERIVED (not one-shot) so an
  // in-session rename immediately drops the badge — and stops publishInvoice from snapshotting
  // verified:true for a name the Firestore rule would reject. .trim() mirrors the rule's trimmed compare.
  const businessVerified = useMemo(
    () =>
      businessVerification?.verified === true &&
      String(businessVerification.verifiedName ?? "").trim() === String(businessProfile?.businessName ?? "").trim(),
    [businessVerification, businessProfile?.businessName],
  );
  const [products, setProducts] = useState<Product[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [dataInitialized, setDataInitialized] = useState(false);
  const [invoiceFooter, setInvoiceFooterState] = useState<string>("");
  const [invoiceTerms, setInvoiceTermsState] = useState<string>("");
  const [reports, setReports] = useState<any[]>([]);
  const [invoiceLayout, setInvoiceLayoutState] = useState<'standard' | 'basic'>('standard');
  const [printQrCode, setPrintQrCodeState] = useState<boolean>(false);

  // Add this state:
  const [labelPrinterLanguageState, setLabelPrinterLanguageState] = useState<"escpos" | "tspl" | "zpl">("escpos");

  // ADD THESE:
  const [labelWidthState, setLabelWidthState] = useState<number>(50);
  const [labelHeightState, setLabelHeightState] = useState<number>(25);
  // Derived legacy-compat from plan.billing
  const subscriptionId = plan?.billing?.subscriptionId || null;
  const subscriptionStatus = (plan?.billing?.status as any) || null;
  const subscriptionPlan = plan?.billing?.cadenceDays ? `${plan.billing.cadenceDays} Days` : null;
  const subscriptionCurrentPeriodEnd = plan?.billing?.nextChargeAt || null;

  useEffect(() => {
    const checkStatus = async () => {
      const status = await Network.getStatus();
      setIsOnline(status.connected);
    };
    checkStatus();

    const listener = Network.addListener('networkStatusChange', (status) => {
      setIsOnline(status.connected);
    });

    return () => {
      listener.then(l => l.remove());
    };
  }, [spreadsheetId, needsTokenRefresh]);

  useEffect(() => {
    if (user) {
      loadUserSettings();
      fetchReports(user.uid);
    } else {
      setSpreadsheetId(null);
      setProducts([]);
      setTransactions([]);
      setReports([]);
      setIsLoading(false);
      setSettingsLoaded(false);
      setDataInitialized(false);
      setProfileCompleted(false);
      setBusinessProfile(null);
      setBusinessVerification(null);
      setInvoiceFooterState("");
      setInvoiceTermsState("");
      // Reset canonical subscription state
      setPlan(null);
      setTrialsUsed(null);
      setDevices(null);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const unsubscribe = subscribeToUserSettings(user.uid, (liveSettings) => {
      setRewardPoints(liveSettings.rewardPoints);
      setReadNotificationIds(liveSettings.readNotificationIds || []);
      setDismissedNotificationIds(liveSettings.dismissedNotificationIds || []);
      setHideFavoriteProductsState(liveSettings.hideFavoriteProducts ?? false);
      setHideSalesSummaryState(liveSettings.hideSalesSummary ?? false);
      setPrinterPaperWidthState((liveSettings.printerPaperWidth as "58" | "80" | "A4") ?? "58");
      setPrinterPrintLogoState(liveSettings.printerPrintLogo ?? true);
      setDeletionRequestedAt(liveSettings.deletionRequestedAt ?? null);
      setDeletionReason(liveSettings.deletionReason ?? "");
      setInvoiceFooterState(liveSettings.invoiceFooter ?? "");
      setInvoiceTermsState(liveSettings.invoiceTerms ?? "");
      setInvoiceLayoutState(liveSettings.invoiceLayout ?? 'standard');
      setPrintQrCodeState(liveSettings.printQrCode ?? false);

      setLabelPrinterLanguageState(liveSettings.labelPrinterLanguage ?? "escpos");
      setLabelWidthState(liveSettings.labelWidth ?? 50);
      setLabelHeightState(liveSettings.labelHeight ?? 25);

      // Canonical subscription shape
      setPlan(liveSettings.plan || null);
      setTrialsUsed(liveSettings.trialsUsed || null);
      setDevices(liveSettings.devices || null);
      setAiSettings(liveSettings.aiSettings || null);
      setUiModeState((liveSettings.uiMode as any) ?? null);

      if (liveSettings.businessName) {
        setBusinessNameState(liveSettings.businessName);
      }
      if (liveSettings.businessLogo !== undefined) {
        setBusinessLogo(liveSettings.businessLogo);
      }
    });

    return () => unsubscribe();
  }, [user]);

  const fetchReports = async (uid: string) => {
    try {
      const q = query(collection(firebaseDb, "users", uid, "reports"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setReports(snap.docs.map(d => d.data()));
    } catch (err) {
      console.error("Error fetching reports:", err);
    }
  };

  const saveReportRecord = async (reportMetadata: any) => {
    if (!user?.uid) return;
    try {
      const reportRef = doc(collection(firebaseDb, "users", user.uid, "reports"));
      const newReport = {
        ...reportMetadata,
        id: reportRef.id,
        createdAt: new Date().toISOString(),
      };
      await setDoc(reportRef, newReport);
      setReports(prev => [newReport, ...prev]);
      // A report download is a natural break — attempt a (capped) interstitial for free users.
      if (!isPaid) void maybeShowInterstitial();
    } catch (err) {
      console.error("Error saving report record:", err);
    }
  };

  const checkTokenValidity = () => {
    const valid = isAccessTokenValid();
    setNeedsTokenRefresh(!valid);
  };

  const loadUserSettings = async () => {
    if (!user) return;
    try {
      setIsLoading(true);
      const [settings, profile, verification] = await Promise.all([
        getUserSettings(user.uid),
        getBusinessProfile(user.uid),
        getBusinessVerification(user.uid),
      ]);

      setRewardPoints(settings?.rewardPoints || 0);
      setInvoiceFooterState(settings?.invoiceFooter || "");
      setInvoiceLayoutState(settings?.invoiceLayout || 'standard');
      setPrintQrCodeState(settings?.printQrCode || false);

      setLabelPrinterLanguageState(settings?.labelPrinterLanguage || "escpos");
      setLabelWidthState(settings?.labelWidth ?? 50);
      setLabelHeightState(settings?.labelHeight ?? 25);

      setInvoiceTermsState(settings?.invoiceTerms || "");
      setReadNotificationIds(settings?.readNotificationIds || []);
      setDismissedNotificationIds(settings?.dismissedNotificationIds || []);
      setHideFavoriteProductsState(settings?.hideFavoriteProducts ?? false);
      setHideSalesSummaryState(settings?.hideSalesSummary ?? false);
      setPrinterPaperWidthState(
        (settings?.printerPaperWidth as "58" | "80" | "A4") ?? "58",
      );
      setPrinterPrintLogoState(settings?.printerPrintLogo ?? true);
      setDeletionRequestedAt(settings?.deletionRequestedAt ?? null);
      setDeletionReason(settings?.deletionReason ?? "");
      setBusinessNameState(
        settings?.businessName || profile?.businessName || "",
      );
      setBusinessLogo(
        settings?.businessLogo !== undefined
          ? settings.businessLogo
          : profile?.businessLogo || null,
      );
      setProfileCompleted(settings?.profileCompleted || false);
      setBusinessProfile(profile || null);

      // Store the raw admin verification; businessVerified is derived (useMemo) from it +
      // the live businessName so a rename immediately invalidates the badge.
      setBusinessVerification(verification);

      // Canonical subscription
      setPlan(settings?.plan || null);
      setTrialsUsed(settings?.trialsUsed || null);
      setDevices(settings?.devices || null);
      setAiSettings((settings as any)?.aiSettings || null);

      if (settings?.spreadsheetId) {
        setSpreadsheetId(settings.spreadsheetId);
      }

      // Critical settings are in (profile / plan / printer / spreadsheet) — release
      // the loader + launch splash NOW. Non-essential work (signup bonus, referral
      // rewards, reward history, FCM token) runs in the background so it never
      // blocks the app from showing on a slow network.
      setSettingsLoaded(true);
      setIsLoading(false);

      const uid = user.uid;
      const isActiveUltimate = settings?.plan?.tier === "ultimate" && !!settings?.plan?.expiresAt && new Date(settings.plan.expiresAt) > new Date();
      (async () => {
        try {
          if (!settings?.signupBonusAwarded) {
            await updateUserSettings(uid, { signupBonusAwarded: true });
          }
        } catch (err) { }
        try {
          const pendingRewards = await getPendingReferralRewards(uid);
          for (const { id, refereeName } of pendingRewards) {
            if (isActiveUltimate) {
              await markReferralRewarded(id);
              continue;
            }
            const newPoints = await atomicUpdateRewardPoints(uid, 500);
            if (newPoints !== -1) setRewardPoints(newPoints);
            await saveRewardEvent(uid, {
              type: "earn",
              points: 500,
              date: new Date().toISOString(),
              description: `Referral bonus — ${refereeName} completed all steps!`,
              action: "referral_bonus",
            });
            await markReferralRewarded(id);
          }
        } catch (err) { }
        try {
          const history = await loadRewardHistory(uid);
          setRewardHistory(history);
        } catch (err) { }
        try {
          if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            const fcmToken = await getTokenForNotifications();
            if (fcmToken) {
              setDoc(
                doc(firebaseDb, "users", uid, "fcm_tokens", fcmToken),
                { token: fcmToken, updatedAt: serverTimestamp(), platform: "web" },
                { merge: true },
              ).catch(() => { });
            }
          }
        } catch (err) { }
      })();
    } catch (err) {
      setError("Failed to load settings");
      setSettingsLoaded(true);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (settingsLoaded && profileCompleted) {
      loadData();
    }
  }, [spreadsheetId, settingsLoaded, needsTokenRefresh, profileCompleted]);

  const loadData = async () => {
    setIsLoading(true);

    // 1. Offline-first: paint instantly from the local cache so the dashboard —
    //    and the launch splash — never block on the network. Firestore refreshes below.
    try {
      const [cachedProducts, cachedTransactions] = await Promise.all([
        localDb.products.toArray(),
        localDb.transactions.toArray(),
      ]);
      if (cachedProducts.length > 0) setProducts(cachedProducts as Product[]);
      if (cachedTransactions.length > 0) setTransactions(cachedTransactions as Transaction[]);
    } catch (dbErr) { }

    // Cache is shown — release the loader / launch splash now; don't wait on Firestore.
    setError(null);
    setIsLoading(false);
    setDataInitialized(true);

    // 2. Refresh from Firestore (authoritative) in the background.
    if (user) {
      try {
        const [productsSnap, transactionsSnap] = await Promise.all([
          getDocs(collection(firebaseDb, "users", user.uid, "products")),
          getDocs(collection(firebaseDb, "users", user.uid, "transactions")),
        ]);
        const fsProducts = productsSnap.docs.map((d) => {
          const data = d.data();
          return {
            id: data.id || d.id,
            name: data.name || "Unknown Product",
            ...data,
            salePrice: data.salePrice ?? data.rate ?? 0,
            costPrice: data.costPrice ?? 0,
            isFavorite: data.isFavorite ?? false,
          } as Product;
        });
        const fsTransactions = transactionsSnap.docs.map(
          (d) => d.data() as Transaction,
        );

        if (fsProducts.length > 0 || fsTransactions.length > 0) {
          setProducts(fsProducts);
          setTransactions(fsTransactions);

          const missingBarcode = fsProducts.filter((p) => !p.barcode);
          if (missingBarcode.length > 0 && user) {
            (async () => {
              for (const p of missingBarcode) {
                await new Promise((r) => setTimeout(r, 50));
                const newBarcode = generateAppBarcode();
                setDoc(doc(firebaseDb, "users", user.uid, "products", p.id), { barcode: newBarcode }, { merge: true }).catch(() => { });
                setProducts((prev) => prev.map((pr) => pr.id === p.id ? { ...pr, barcode: newBarcode } : pr));
              }
            })();
          }
        }
      } catch (fsErr) { }
    }
  };

  const createProduct = async (
    product: Omit<Product, "id"> & {
      isFromExternal?: boolean;
      hsn?: string;
      gstRate?: number;
      taxType?: string;
      gstInclusive?: boolean;
    },
  ) => {
    const { isFromExternal, ...productData } = product;
    const id = `p_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const finalBarcode = productData.barcode || generateAppBarcode();
    const tempProduct = {
      ...productData,
      id,
      barcode: finalBarcode,
      syncStatus: "pending" as const,
      hsn: product.hsn || "",
      gstRate: product.gstRate || 0,
      taxType: product.taxType || "none",
      gstInclusive: product.gstInclusive || false,
      isFromExternal: isFromExternal ?? false,
    };

    await localDb.products.add(tempProduct as any);
    setProducts((prev) => [...prev, tempProduct as Product]);

    if (user) {
      setDoc(doc(firebaseDb, "users", user.uid, "products", id), {
        id,
        name: productData.name,
        rate: productData.rate,
        salePrice: (productData as any).salePrice ?? productData.rate ?? 0,
        costPrice: (productData as any).costPrice ?? 0,
        category: productData.category || "",
        unit: productData.unit || "",
        barcode: finalBarcode,
        stock: productData.stock || 0,
        hsn: product.hsn || "",
        gstRate: product.gstRate || 0,
        taxType: product.taxType || "none",
        gstInclusive: product.gstInclusive || false,
        isFavorite: false,
        isFromExternal: isFromExternal ?? false,
      }).catch(() => { });
    }

    const rewardPts = isFromExternal ? 5 : 1;
    const rewardDesc = isFromExternal
      ? `Product added (Barcode): ${productData.name}`
      : `Product added: ${productData.name}`;
    const rewardAction = isFromExternal ? "barcode_add" : "manual_add";
    updateRewardPoints(rewardPts, rewardDesc, rewardAction).catch(() => { });

    if (user) updateReferralStep(user.uid, "addedProduct").catch(() => { });

    if (spreadsheetId) {
      try {
        const newProduct = await addProduct(
          spreadsheetId,
          tempProduct as Product,
          isFromExternal,
        );
        await localDb.products.update(id, {
          ...newProduct,
          syncStatus: "synced",
        });
        setProducts((prev) => prev.map((p) => (p.id === id ? newProduct : p)));
        return newProduct;
      } catch (err) { }
    }

    return tempProduct as Product;
  };

  const editProduct = async (product: Product) => {
    const updatedWithGst = {
      ...product,
      hsn: product.hsn || "",
      gstRate: product.gstRate || 0,
      taxType: product.taxType || "none",
      gstInclusive: product.gstInclusive || false,
      syncStatus: "pending" as const,
    };

    await localDb.products.update(product.id, updatedWithGst as any);
    setProducts((prev) =>
      prev.map((p) =>
        p.id === product.id ? ({ ...p, ...updatedWithGst } as Product) : p,
      ),
    );

    if (user) {
      setDoc(doc(firebaseDb, "users", user.uid, "products", product.id), {
        id: product.id,
        name: product.name || "",
        rate: Number(product.rate) || 0,
        salePrice: Number((product as any).salePrice) || Number(product.rate) || 0,
        costPrice: Number((product as any).costPrice) || 0,
        category: product.category || "",
        unit: product.unit || "",
        barcode: product.barcode || "",
        stock: Number(product.stock) || 0,
        hsn: product.hsn || "",
        gstRate: Number(product.gstRate) || 0,
        taxType: product.taxType || "none",
        gstInclusive: product.gstInclusive || false,
        isFavorite: product.isFavorite || false,
        imageUrl: product.imageUrl || "",
      },
        { merge: true },
      ).catch(() => { });
    }

    if (spreadsheetId) {
      try {
        const updated = await updateProduct(spreadsheetId, updatedWithGst as Product);
        const mergedUpdated = { ...updated, syncStatus: "synced" as const };
        await localDb.products.update(product.id, mergedUpdated as any);
        setProducts((prev) => prev.map((p) => p.id === product.id ? ({ ...p, ...mergedUpdated } as Product) : p));
        return mergedUpdated as Product;
      } catch (err) { }
    }

    return updatedWithGst as Product;
  };

  const removeProduct = async (productId: string) => {
    const product = products.find((p) => p.id === productId);

    await localDb.products.delete(productId);
    setProducts((prev) => prev.filter((p) => p.id !== productId));

    if (user) {
      deleteDoc(doc(firebaseDb, "users", user.uid, "products", productId)).catch(() => { });
    }

    if (product) {
      const earnEntry = rewardHistory.find(
        (h) => (h.action === "barcode_add" || h.action === "manual_add") && h.description.includes(product.name),
      );
      const pointsToDeduct = earnEntry ? -Math.abs(earnEntry.points) : -1;
      updateRewardPoints(pointsToDeduct, `Product deleted: ${product.name}`, "delete_product").catch(() => { });
    }

    if (spreadsheetId) {
      try {
        await deleteProduct(spreadsheetId, productId);
      } catch (err) {
        await localDb.pendingOps.add({
          opType: "delete_product",
          targetId: productId,
          createdAt: new Date().toISOString(),
        });
      }
    }
    toastify.success("Product deleted successfully");
  };

  const createTransactions = async (
    items: {
      productId: string;
      productName: string;
      quantity: number;
      rate: number;
    }[],
    type: "sale" | "expense",
    generateInvoice: boolean = false,
    isGstBill: boolean = false,
  ) => {
    const now = new Date().toISOString();
    let orderId = `order_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    let finalOrderId = orderId;

    if (isGstBill && user) {
      const gstNumber = await getNextGstInvoiceNumber(user.uid);
      const cleanGstNumber = gstNumber.startsWith("V-") ? gstNumber.slice(2) : gstNumber;
      finalOrderId = `INV-${cleanGstNumber}`;
    }

    const mainItems = items.filter(i => i.productName !== "Discount" && i.productName !== "Bill Discount");
    const grossInvoiceTotal = mainItems.reduce((sum, i) => sum + (i.quantity * i.rate), 0);
    const discountItem = items.find(i => i.productName === "Bill Discount" || i.productName === "Discount");
    const flatDiscount = discountItem ? Math.abs(discountItem.rate * discountItem.quantity) : 0;

    const newLocalTransactions = items.map((item, i) => {
      // 1. HANDLE THE DISCOUNT DOCUMENT
      if (item.productName === "Discount" || item.productName === "Bill Discount") {
        return {
          id: `${finalOrderId}_${i}`,
          fullOrderId: finalOrderId,
          productId: item.productId,
          productName: item.productName,
          quantity: item.quantity,
          rate: item.rate,
          total: item.quantity * item.rate,
          type: type,
          createdAt: now,
          syncStatus: "pending" as const,
          cgst: 0, sgst: 0, hsn: "", isGstBill: false,
          taxableAmount: 0, // <-- ADDED: Discount record gets 0
        };
      }

      // 2. HANDLE THE PRODUCT DOCUMENTS
      const product = products.find((p) => p.id === item.productId);
      const gstRate = (product as any)?.gstRate || 0;
      const productGstInclusive = (product as any)?.gstInclusive || false;

      const rawLineTotal = item.quantity * item.rate;
      const proportion = grossInvoiceTotal > 0 ? (rawLineTotal / grossInvoiceTotal) : 0;
      const discountedLineTotal = rawLineTotal - (flatDiscount * proportion);

      let finalLineTotal = rawLineTotal;
      let cgst = 0;
      let sgst = 0;
      let dbTaxableValue = discountedLineTotal; // <-- ADDED: Default to discounted base

      if (isGstBill && type === "sale") {
        if (gstRate > 0) {
          if (productGstInclusive) {
            dbTaxableValue = discountedLineTotal / (1 + gstRate / 100); // <-- ADDED: Inclusive base
            const totalTax = discountedLineTotal - dbTaxableValue;
            cgst = Math.round((totalTax / 2 + Number.EPSILON) * 100) / 100;
            sgst = Math.round((totalTax / 2 + Number.EPSILON) * 100) / 100;
          } else {
            dbTaxableValue = discountedLineTotal; // <-- ADDED: Exclusive base
            const totalTax = dbTaxableValue * (gstRate / 100);
            cgst = Math.round((totalTax / 2 + Number.EPSILON) * 100) / 100;
            sgst = Math.round((totalTax / 2 + Number.EPSILON) * 100) / 100;

            finalLineTotal = Math.round((dbTaxableValue + cgst + sgst + Number.EPSILON) * 100) / 100;
          }
        }
      }

      // FIX 2026-05-02: For non-GST and zero-GST lines, the if-block above leaves
      // finalLineTotal === rawLineTotal (pre-discount). Storing the pre-discount
      // value in `total` while taxableAmount holds the post-discount value caused
      // the mobile RecordSuccessPopup threshold check to print pre-discount rates
      // for tiny per-line discount shares (≤ ₹0.10), inflating the receipt by
      // exactly the un-applied portion of the discount. When finalLineTotal was
      // not adjusted for GST (i.e. equals rawLineTotal), use discountedLineTotal
      // so total and taxableAmount agree.
      const persistedLineTotal = finalLineTotal === rawLineTotal ? discountedLineTotal : finalLineTotal;
      return {
        id: `${finalOrderId}_${i}`,
        fullOrderId: finalOrderId,
        productId: item.productId,
        productName: item.productName,
        quantity: item.quantity,
        rate: item.rate,
        total: type === "expense" ? -Number(persistedLineTotal.toFixed(2)) : Number(persistedLineTotal.toFixed(2)),
        type: type,
        createdAt: now,
        syncStatus: "pending" as const,
        cgst: Number(cgst.toFixed(2)),
        sgst: Number(sgst.toFixed(2)),
        hsn: (product as any)?.hsn || "",
        isGstBill: isGstBill,
        taxableAmount: Number(dbTaxableValue.toFixed(2)), // <-- ADDED: Exact calculated base
      };
    });

    try {
      await localDb.transactions.bulkAdd(newLocalTransactions as any);
    } catch (e) { }

    setTransactions((prev) => [...(newLocalTransactions as Transaction[]), ...prev]);

    if (user) {
      Promise.all(newLocalTransactions.map(txn =>
        setDoc(doc(firebaseDb, "users", user.uid, "transactions", txn.id), {
          id: txn.id,
          fullOrderId: txn.fullOrderId,
          productId: txn.productId,
          productName: txn.productName,
          quantity: txn.quantity,
          rate: txn.rate,
          total: txn.total,
          type: txn.type,
          createdAt: txn.createdAt,
          cgst: txn.cgst,
          sgst: txn.sgst,
          hsn: txn.hsn,
          isGstBill: txn.isGstBill,
          taxableAmount: (txn as any).taxableAmount || 0, // <-- ADDED: Save to cloud
        })
      )).catch(() => { });
    }

    for (const item of items) {
      const product = products.find((p) => p.id === item.productId);
      if (product && type === "sale") {
        const newStock = (product.stock || 0) - item.quantity;
        const updatedProduct = { ...product, stock: newStock };

        await localDb.products.update(product.id, {
          stock: newStock,
          syncStatus: "pending",
        });

        setProducts((prev) => prev.map((p) => (p.id === product.id ? updatedProduct : p)));

        if (user) {
          setDoc(doc(firebaseDb, "users", user.uid, "products", product.id), { stock: newStock }, { merge: true }).catch(() => { });
        }
      }
    }

    if (user && type === "sale") {
      updateReferralStep(user.uid, "madeSale").catch(() => { });
    }

    if (userMode !== "pro" && type === "sale") {
      const points = generateInvoice ? 2 : 1;
      const description = `Sale Recorded: ${items.map((i) => i.productName).join(", ")}`;
      const action = generateInvoice ? "record_and_share" : "record_only";
      updateRewardPoints(points, description, action, finalOrderId).catch(() => { });
    }

    if (spreadsheetId) {
      const backgroundSync = async () => {
        try {
          await addTransactions(spreadsheetId, items, type, finalOrderId);
          const itemIds = newLocalTransactions.map((t) => t.id);
          await localDb.transactions.where("id").anyOf(itemIds).modify({ syncStatus: "synced" });

          await Promise.all(
            items
              .filter((item) => item.productId && item.quantity > 0)
              .map(async (item) => {
                try {
                  await updateProductStock(spreadsheetId, item.productId, item.quantity, type === "sale" ? "deduct" : "add");
                } catch (err) { }
              })
          );
        } catch (err) { }
      };
      backgroundSync();
    }
    return newLocalTransactions as Transaction[];
  };

  const createStockExpense = async (item: {
    productId: string;
    productName: string;
    quantity: number;
    rate: number;
  }) => {
    const now = new Date().toISOString();
    const orderId = `stock_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const total = -(item.quantity * item.rate);
    const txn = {
      id: `${orderId}_0`,
      fullOrderId: orderId,
      productId: item.productId,
      productName: item.productName,
      quantity: item.quantity,
      rate: item.rate,
      total,
      type: "expense" as const,
      createdAt: now,
      syncStatus: "pending" as const,
      cgst: 0,
      sgst: 0,
      hsn: "",
      isGstBill: false,
    };

    try {
      await localDb.transactions.add(txn as any);
    } catch (e) { }

    setTransactions((prev) => [txn as Transaction, ...prev]);

    if (user) {
      setDoc(doc(firebaseDb, "users", user.uid, "transactions", txn.id), {
        id: txn.id,
        fullOrderId: txn.fullOrderId,
        productId: txn.productId,
        productName: txn.productName,
        quantity: txn.quantity,
        rate: txn.rate,
        total: txn.total,
        type: txn.type,
        createdAt: txn.createdAt,
        cgst: txn.cgst,
        sgst: txn.sgst,
        hsn: txn.hsn,
        isGstBill: txn.isGstBill,
      }).catch(() => { });
    }

    if (spreadsheetId) {
      try {
        await addTransactions(spreadsheetId, [item], "expense", orderId);
      } catch (e) { }
    }
  };

  const connectSpreadsheet = async (id: string) => {
    if (!user) throw new Error("Not authenticated");
    await updateUserSettings(user.uid, {
      spreadsheetId: id,
      isSyncEnabled: true,
    });
    setSpreadsheetId(id);
  };

  const refreshToken = async () => {
    try {
      await refreshGoogleToken();
      setNeedsTokenRefresh(false);
      if (spreadsheetId) await loadData();
    } catch (err) {
      throw err;
    }
  };

  const refreshProducts = async () => {
    if (!user) return;
    try {
      const snap = await getDocs(
        collection(firebaseDb, "users", user.uid, "products"),
      );
      const fsProducts = snap.docs.map((d) => d.data() as Product);
      if (fsProducts.length > 0) {
        setProducts(fsProducts);
      }
    } catch (err) { }
  };

  const refreshTransactions = async () => {
    if (!user) return;
    try {
      const snap = await getDocs(
        collection(firebaseDb, "users", user.uid, "transactions"),
      );
      const fsTransactions = snap.docs.map((d) => d.data() as Transaction);
      if (fsTransactions.length > 0) {
        setTransactions(fsTransactions);
      }
    } catch (err) { }
  };

  const refreshAll = async () => {
    setIsLoading(true);
    await Promise.all([refreshProducts(), refreshTransactions()]);
    setIsLoading(false);
  };

  const disconnectSpreadsheet = async () => {
    await updateUserSettings(user!.uid, {
      spreadsheetId: "",
      isSyncEnabled: false,
    });
    setSpreadsheetId(null);
  };

  const createNewSpreadsheet = async () => {
    const s = await createSpreadsheet("DailyHisaab_DB");
    connectSpreadsheet(s.spreadsheetId);
  };

  const listSpreadsheets = useCallback(() => listUserSpreadsheets(), []);

  const setUserMode = async (m: UserMode) => {
    // Pro/Ultimate users can opt into "normal" UI view via settings.uiMode.
    // Persist immediately for snappy local toggle, then write Firestore.
    setUiModeState(m as any);
    if (!user) return;
    try {
      await updateUserSettings(user.uid, { uiMode: m as any });
    } catch (err) {
      console.error("[DataContext] setUserMode write failed:", err);
    }
  };

  const setBusinessName = async (n: string) => {
    await updateUserSettings(user!.uid, { businessName: n });
    setBusinessNameState(n);
  };

  const saveBusinessProfile = async (p: BusinessProfile) => {
    try {
      await saveBusinessProfileToFirestore(user!.uid, { ...p, businessLogo });
    } catch (err) {
      console.error("Failed to save profile:", err);
      toast({ title: "Save failed", description: (err as any)?.message || "Check your internet connection", variant: "destructive" });
      throw err;
    }
    setBusinessProfile(p);
    setBusinessNameState(p.businessName);
    setProfileCompleted(true);
  };

  const updateBusinessLogo = async (l: string | null) => {
    await updateUserSettings(user!.uid, { businessLogo: l });
    if (l === null) {
      await clearBusinessProfileLogo(user!.uid);
    }
    setBusinessLogo(l);
  };

  const requestDriveAccess = async () => {
    const token = await requestDriveAccessFromFirebase();
    if (!token) throw new Error("Failed to get Drive access");
    try {
      const files = await listUserSpreadsheets();
      const existingSheet = files?.find(
        (f: any) => f.name === "DailyHisaab_DB",
      );
      if (existingSheet) {
        await connectSpreadsheet(existingSheet.id);
      } else {
        const newSheet = await createSpreadsheet("DailyHisaab_DB");
        await connectSpreadsheet(newSheet.spreadsheetId);
      }
    } catch (sheetErr) {
      throw sheetErr;
    }
  };

  const deleteTransaction = async (id: string) => {
    const txn = transactions.find((t) => t.id === id);
    const isGstBill = !!(txn as any)?.isGstBill;
    const cancelledAt = new Date().toISOString();

    // GST bills: soft-delete to preserve invoice serial sequence (CGST Rule 46(b)).
    // Non-GST bills: hard-delete (no compliance constraint).
    if (isGstBill) {
      setTransactions((t) => t.map((x) => x.id === id ? ({ ...x, cancelled: true, cancelledAt } as any) : x));
      localDb.transactions.where("id").equals(id).modify({ cancelled: true, cancelledAt }).catch(() => { });
      if (user) {
        setDoc(doc(firebaseDb, "users", user.uid, "transactions", id), { cancelled: true, cancelledAt }, { merge: true }).catch(() => { });
      }
    } else {
      setTransactions((t) => t.filter((x) => x.id !== id));
      localDb.transactions.where("id").equals(id).delete().catch(() => { });
      if (user) {
        deleteDoc(doc(firebaseDb, "users", user.uid, "transactions", id)).catch(() => { });
      }
    }

    if (txn && txn.type === "sale") {
      const product = products.find((p) => p.id === txn.productId);
      if (product) {
        const restoredStock = (product.stock || 0) + txn.quantity;
        const updatedProduct = { ...product, stock: restoredStock };

        setProducts((prev) =>
          prev.map((p) => (p.id === product.id ? updatedProduct : p)),
        );

        localDb.products.update(product.id, { stock: restoredStock, syncStatus: "pending" }).catch(() => { });

        if (user) {
          setDoc(doc(firebaseDb, "users", user.uid, "products", product.id), { stock: restoredStock }, { merge: true }).catch(() => { });
        }

        updateProductStock(spreadsheetId!, product.id, txn.quantity, "add").catch(() => { });
      }
    }

    if (txn) {
      const orderPrefix = id.replace(/_\d+$/, "");

      const remainingForOrder = transactions.filter(
        (t) =>
          t.id !== id &&
          (t.fullOrderId === orderPrefix ||
            t.id.replace(/_\d+$/, "") === orderPrefix),
      );
      // Public-invoice doc stays alive for cancelled GST bills so the share link still resolves
      // (with a "cancelled" banner). Hard-deletes still clear it as before.
      if (remainingForOrder.length === 0 && user && !isGstBill) {
        deletePublicInvoice(user.uid, orderPrefix).catch(() => { });
      }

      if (!deductedOrderIds.has(orderPrefix)) {
        deductedOrderIds.add(orderPrefix);
        const earnEntry = rewardHistory.find(
          (h) =>
            (h.action === "record_and_share" || h.action === "record_only") &&
            h.orderId === orderPrefix,
        );
        const pointsToDeduct = earnEntry ? -Math.abs(earnEntry.points) : -1;
        updateRewardPoints(
          pointsToDeduct,
          `Transaction deleted: ${txn.productName}`,
          "delete_transaction",
        ).catch(() => { });
      }
    }

    try {
      await deleteTransactionFromSheet(spreadsheetId!, id);
    } catch (err) {
      await localDb.pendingOps.add({
        opType: "delete_transaction",
        targetId: id,
        createdAt: new Date().toISOString(),
      });
    }
  };

  const publishInvoice = async (orderId: string): Promise<string | null> => {
    if (!user) return null;
    const safeOrderId = String(orderId || "");
    if (!safeOrderId) return null;

    try {
      const orderTxns = transactions.filter((t) => {
        const txnOrderId = t.fullOrderId || t.id.replace(/_\d+$/, "");
        return txnOrderId === safeOrderId;
      });
      if (orderTxns.length === 0) return null;

      const totalWithTax = orderTxns.reduce((sum, t) => sum + t.total, 0);
      const isGstBill = safeOrderId.startsWith("INV-") || orderTxns[0]?.isGstBill || false;

      const fullInvoiceData = {
        orderId: safeOrderId,
        userId: user.uid,
        // Snapshotted top-level flag — public surfaces can't read the
        // verifications doc, so they gate on data.verified === true.
        verified: businessVerified,
        transactions: orderTxns,
        createdAt: orderTxns[0]?.createdAt || serverTimestamp(),
        isGstEnabled: isGstBill,
        gstin: businessProfile?.gstin || "",
        cin: businessProfile?.cin || "",
        fssai: businessProfile?.fssai || "",
        total: totalWithTax,
        invoiceFooter: invoiceFooter || "",
        invoiceTerms: invoiceTerms || "",
        businessProfile: {
          businessName: businessProfile?.businessName || businessName || "Retail Store",
          businessAddress: businessProfile?.businessAddress || "",
          businessPhone: businessProfile?.businessPhone || "",
          businessEmail: businessProfile?.businessEmail || "",
          businessWebsite: businessProfile?.businessWebsite || "",
          businessLogo: businessProfile?.businessLogo || businessLogo || null,
          isGstEnabled: isGstBill,
          gstin: businessProfile?.gstin || "",
          cin: businessProfile?.cin || "",
          fssai: businessProfile?.fssai || "",
          invoiceFooter: invoiceFooter || "",
          invoiceTerms: invoiceTerms || "",
        },
      };

      await publishInvoiceToFirestore(user.uid, safeOrderId, fullInvoiceData);

      const shortId = Math.random().toString(36).substring(2, 8).toUpperCase();

      const pointerRef = doc(firebaseDb, "public_invoices", shortId);
      await setDoc(pointerRef, {
        isPointer: true,
        userId: user.uid,
        orderId: safeOrderId,
        createdAt: serverTimestamp()
      });

      const productionDomain = "https://dailyhisaab.com";
      return `${productionDomain}/bill/${shortId}`;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  const setInvoiceFooter = async (value: string) => {
    setInvoiceFooterState(value);
    if (user) await updateUserSettings(user.uid, { invoiceFooter: value });
  };

  const setInvoiceTerms = async (value: string) => {
    setInvoiceTermsState(value);
    if (user) await updateUserSettings(user.uid, { invoiceTerms: value });
  };

  const setInvoiceLayout = async (value: 'standard' | 'basic') => {
    setInvoiceLayoutState(value);
    if (user) {
      try {
        await updateUserSettings(user.uid, { invoiceLayout: value });
      } catch (err) {
        console.error("Failed to save layout preference to Firestore:", err);
      }
    }
  };

  const setPrintQrCode = async (value: boolean) => {
    setPrintQrCodeState(value);
    if (user) {
      try {
        await updateUserSettings(user.uid, { printQrCode: value });
      } catch (err) {
        console.error("Failed to save QR code preference:", err);
      }
    }
  };

  // Add this new function:
  const setLabelPrinterLanguage = async (value: "escpos" | "tspl" | "zpl") => {
    setLabelPrinterLanguageState(value);
    if (user) {
      try {
        await updateUserSettings(user.uid, { labelPrinterLanguage: value });
      } catch (err) {
        console.error("Failed to save label printer language:", err);
      }
    }
  };

  const setLabelSize = async (width: number, height: number) => {
    setLabelWidthState(width);
    setLabelHeightState(height);
    if (user) {
      try {
        await updateUserSettings(user.uid, { labelWidth: width, labelHeight: height });
      } catch (err) {
        console.error("Failed to save label size:", err);
      }
    }
  };

  const convertToGstBill = async (
    transactionIds: string[],
  ): Promise<string | null> => {
    if (!user || transactionIds.length === 0) return null;

    const firstTxn = transactions.find((t) => transactionIds.includes(t.id));
    if (firstTxn) {
      const existingOrderId =
        firstTxn.fullOrderId || firstTxn.id.replace(/_\d+$/, "");
      if (existingOrderId.startsWith("INV-")) {
        return existingOrderId;
      }
    }

    try {
      const gstNumber = await getNextGstInvoiceNumber(user.uid);
      const cleanGstNumber = gstNumber.startsWith("V-")
        ? gstNumber.slice(2)
        : gstNumber;
      const newOrderId = `INV-${cleanGstNumber}`;

      const oldOrderId =
        firstTxn?.fullOrderId || firstTxn?.id.replace(/_\d+$/, "") || "";

      const oldTxns = transactions.filter(x => transactionIds.includes(x.id));
      const mainItems = oldTxns.filter(i => !i.productName.toLowerCase().includes("discount"));
      const grossInvoiceTotal = mainItems.reduce((sum, i) => sum + (i.quantity * i.rate), 0);
      const discountItem = oldTxns.find(i => i.productName.toLowerCase().includes("discount"));
      const flatDiscount = discountItem ? Math.abs(Number(discountItem.total) || (discountItem.quantity * discountItem.rate)) : 0;

      const updatedTransactions = transactions.map((t) => {
        if (!transactionIds.includes(t.id)) return t;

        const idx = transactionIds.indexOf(t.id);

        if (t.productName.toLowerCase().includes("discount")) {
          return {
            ...t,
            id: `${newOrderId}_${idx}`,
            fullOrderId: newOrderId,
            isGstBill: true,
            syncStatus: "pending" as const,
            taxableAmount: 0
          };
        }

        const product = products.find((p) => p.id === t.productId);
        const gstRate = (product as any)?.gstRate || 0;
        const productGstInclusive = (product as any)?.gstInclusive || false;

        // FIX: In converted bills, t.rate is already discounted. We must use the true product rate to find the gross total.
        const originalGrossRate = product?.salePrice || product?.rate || t.rate;
        const rawLineTotal = t.quantity * originalGrossRate;
        const proportion = grossInvoiceTotal > 0 ? (rawLineTotal / grossInvoiceTotal) : 0;
        const discountedLineTotal = rawLineTotal - (flatDiscount * proportion);

        let cgst = 0;
        let sgst = 0;
        let dbTaxableValue = discountedLineTotal;

        if (gstRate > 0) {
          if (productGstInclusive) {
            dbTaxableValue = discountedLineTotal / (1 + gstRate / 100);
            const totalTax = discountedLineTotal - dbTaxableValue;
            cgst = Math.round((totalTax / 2 + Number.EPSILON) * 100) / 100;
            sgst = Math.round((totalTax / 2 + Number.EPSILON) * 100) / 100;
          } else {
            dbTaxableValue = discountedLineTotal;
            const totalTax = dbTaxableValue * (gstRate / 100);
            cgst = Math.round((totalTax / 2 + Number.EPSILON) * 100) / 100;
            sgst = Math.round((totalTax / 2 + Number.EPSILON) * 100) / 100;
          }
        }

        const newTotal = !productGstInclusive
          ? Math.round((rawLineTotal + cgst + sgst + Number.EPSILON) * 100) / 100
          : rawLineTotal;

        return {
          ...t,
          id: `${newOrderId}_${idx}`,
          fullOrderId: newOrderId,
          isGstBill: true,
          total: newTotal,
          cgst: Number(cgst.toFixed(2)),
          sgst: Number(sgst.toFixed(2)),
          gstRate,
          gstInclusive: productGstInclusive,
          syncStatus: "pending" as const,
          taxableAmount: Number(dbTaxableValue.toFixed(2))
        };
      });

      for (const oldId of transactionIds) {
        try {
          await localDb.transactions.delete(oldId);
          deleteDoc(doc(firebaseDb, "users", user.uid, "transactions", oldId)).catch(() => { });
        } catch { }
      }

      const newTxns = updatedTransactions.filter(
        (t) => t.fullOrderId === newOrderId,
      );
      try {
        await localDb.transactions.bulkAdd(newTxns as any);
      } catch { }

      for (const txn of newTxns) {
        setDoc(
          doc(firebaseDb, "users", user.uid, "transactions", txn.id),
          {
            id: txn.id,
            fullOrderId: txn.fullOrderId,
            productId: txn.productId,
            productName: txn.productName,
            quantity: txn.quantity,
            rate: txn.rate,
            total: txn.total,
            type: txn.type,
            createdAt: txn.createdAt,
            cgst: txn.cgst,
            sgst: txn.sgst,
            hsn: (txn as any).hsn || "",
            isGstBill: true,
            gstRate: (txn as any).gstRate || 0,
            gstInclusive: (txn as any).gstInclusive || false,
            taxableAmount: (txn as any).taxableAmount || 0,
          }
        ).catch(() => { });
      }

      if (oldOrderId) {
        deletePublicInvoice(user.uid, oldOrderId).catch(() => { });
      }

      setTransactions(updatedTransactions);
      return newOrderId;
    } catch (err) {
      return null;
    }
  };

  const updateRewardPoints = async (
    points: number,
    description?: string,
    action?: string,
    orderId?: string,
  ) => {
    if (!user) return;
    // Ultimate plan users don't earn/burn points — they use DailyHisaab Store (cash).
    // Their pre-Ultimate balance is preserved but frozen; resumes if they downgrade to Pro/Basic.
    const isActiveUltimate = plan?.tier === "ultimate" && plan?.expiresAt && new Date(plan.expiresAt) > new Date();
    if (isActiveUltimate) return;
    const newPoints = await atomicUpdateRewardPoints(user.uid, points);

    if (newPoints === -1) {
      setRewardPoints((prev) => Math.max(0, prev + points));
    } else {
      setRewardPoints(newPoints);
    }

    const event: Omit<RewardEvent, "id"> = {
      type: points >= 0 ? "earn" : "burn",
      points,
      date: new Date().toISOString(),
      description:
        description || (points >= 0 ? "Points earned" : "Points redeemed"),
      action: action || "unknown",
      ...(orderId ? { orderId } : {}),
    };

    try {
      await saveRewardEvent(user.uid, event);
      setRewardHistory((prev) => [
        { ...event, id: `local_${Date.now()}` },
        ...prev,
      ]);
    } catch (err) { }
  };

  const upgradeToProWithPoints = async (plan: {
    points: number;
    days: number;
  }): Promise<boolean> => {
    if (!user || rewardPoints < plan.points) return false;

    const currentExpiry = proModeExpiry ? new Date(proModeExpiry) : new Date();
    const baseDate = currentExpiry > new Date() ? currentExpiry : new Date();

    const newExpiry = new Date(baseDate);
    newExpiry.setDate(newExpiry.getDate() + plan.days);
    const expiryStr = newExpiry.toISOString();

    try {
      const userRef = doc(firebaseDb, "users", user.uid);
      const existingPlan = plan || { tier: "basic", expiresAt: null, trial: null, billing: null, oneTime: null, history: [] };
      const history = [
        ...(existingPlan.history || []).slice(-9),
        {
          at: new Date().toISOString(),
          action: "points_redeem_pro",
          fromTier: existingPlan.tier || "basic",
          toTier: "pro",
          reason: `Redeemed ${plan.points} points for ${plan.days} days`,
        },
      ];
      await setDoc(userRef, {
        settings: {
          plan: {
            ...existingPlan,
            tier: "pro",
            expiresAt: expiryStr,
            trial: null,
            history,
          },
        }
      }, { merge: true });

      await updateRewardPoints(
        -plan.points,
        `Pro mode ${proModeExpiry && new Date(proModeExpiry) > new Date() ? 'extension' : 'upgrade'}: ${plan.days} day${plan.days > 1 ? "s" : ""}`,
        "pro_upgrade",
      );

      setProExpired(false);

      return true;
    } catch (err) {
      console.error("Critical: Failed to save pro expiry", err);
      return false;
    }
  };

  const redeemPhysicalReward = async (rewardId: string, rewardName: string, points: number, shippingDetails?: any) => {
    if (!user || rewardPoints < points) return false;

    try {
      await updateRewardPoints(
        -points,
        `Redeemed: ${rewardName}`,
        "physical_reward"
      );

      const name = businessProfile?.ownerName || businessProfile?.businessName || user.displayName || "Valued User";
      const email = businessProfile?.businessEmail || user.email || user.phoneNumber || "No Email";

      const sendPhysicalRewardAlert = httpsCallable(functionsAsia, 'triggerPhysicalRewardEmail');
      await sendPhysicalRewardAlert({
        userId: user.uid,
        userName: name,
        userEmail: email,
        rewardId,
        rewardName,
        points,
        shippingDetails: shippingDetails || null
      });

      return true;
    } catch (err) {
      console.error("Failed to redeem physical reward:", err);
      return false;
    }
  };

  const checkProExpiry = () => {
    // Gated on isPaid (not userMode, which is now always "pro"). An expired plan
    // seamlessly becomes free-with-ads — no blocking popup. This stays effectively
    // dormant because isPaid is only true while the plan is still active.
    if (isPaid && proModeExpiry) {
      const expiry = new Date(proModeExpiry);
      if (expiry <= new Date()) {
        setProExpired(true);
      }
    }
  };

  const updateNotificationReadIds = async (ids: string[]) => {
    if (!user) return;
    setReadNotificationIds(ids);
    await updateUserSettings(user.uid, { readNotificationIds: ids });
  };

  const dismissNotificationIds = async (ids: string[]) => {
    if (!user) return;
    const next = Array.from(new Set([...dismissedNotificationIds, ...ids]));
    setDismissedNotificationIds(next);
    await updateUserSettings(user.uid, { dismissedNotificationIds: next });
  };

  const setHideFavoriteProducts = async (value: boolean) => {
    setHideFavoriteProductsState(value);
    if (user)
      await updateUserSettings(user.uid, { hideFavoriteProducts: value });
  };

  const setHideSalesSummary = async (value: boolean) => {
    setHideSalesSummaryState(value);
    if (user) await updateUserSettings(user.uid, { hideSalesSummary: value });
  };

  const setPrinterPaperWidth = async (value: "58" | "80" | "A4") => {
    setPrinterPaperWidthState(value);
    if (user) await updateUserSettings(user.uid, { printerPaperWidth: value });
  };

  const setPrinterPrintLogo = async (value: boolean) => {
    setPrinterPrintLogoState(value);
    if (user) await updateUserSettings(user.uid, { printerPrintLogo: value });
  };

  const requestAccountDeletion = async (reason: string) => {
    const at = new Date().toISOString();
    setDeletionRequestedAt(at);
    setDeletionReason(reason);
    if (user)
      await updateUserSettings(user.uid, {
        deletionRequestedAt: at,
        deletionReason: reason,
      });
  };

  const revokeAccountDeletion = async () => {
    setDeletionRequestedAt(null);
    setDeletionReason("");
    if (user)
      await updateUserSettings(user.uid, {
        deletionRequestedAt: null,
        deletionReason: "",
      });
  };

  const dismissProExpiry = async (_switchToNormal: boolean) => {
    // Tier is derived from plan.tier + expiresAt. Expiry auto-handled by server cron.
    // Client only dismisses local popup state.
    setProExpired(false);
  };

  useEffect(() => {
    if (isPaid && proModeExpiry) {
      checkProExpiry();
      const interval = setInterval(checkProExpiry, 60000);
      return () => clearInterval(interval);
    }
  }, [isPaid, proModeExpiry]);

  // KPI / chart aggregates exclude cancelled GST invoices (see Part 1 of the work plan).
  const liveTransactions = useMemo(
    () => transactions.filter((t) => !(t as any).cancelled),
    [transactions],
  );
  const dashboardStats = useMemo(
    () =>
      liveTransactions.length > 0 ? calculateDashboardStats(liveTransactions) : null,
    [liveTransactions],
  );
  const chartData = useMemo(
    () => calculateWeeklyChartData(liveTransactions),
    [liveTransactions],
  );

  return (
    <DataContext.Provider
      value={{
        invoiceFooter,
        setInvoiceFooter,
        invoiceTerms,
        setInvoiceTerms,
        invoiceLayout,
        setInvoiceLayout,
        printQrCode,
        setPrintQrCode,
        isOnline,
        spreadsheetId,
        isSheetConnected: !!spreadsheetId,
        isLoading,
        dataInitialized,
        needsTokenRefresh,
        userMode,
        isPaid,
        isUltimateOwnerMobile,
        aiSettings,
        rewardPoints,
        rewardHistory,
        proModeExpiry,
        updateUserMode,
        businessName,
        businessLogo,
        profileCompleted,
        businessProfile,
        businessVerified,
        products,
        transactions,
        dashboardStats,
        chartData,
        reports,
        saveReportRecord,
        refreshProducts,
        refreshTransactions,
        refreshAll,
        refreshToken,
        createProduct,
        editProduct,
        removeProduct,
        createTransactions,
        createStockExpense,
        deleteTransaction,
        publishInvoice,
        convertToGstBill,
        connectSpreadsheet,
        disconnectSpreadsheet,
        createNewSpreadsheet,
        listSpreadsheets,
        setUserMode,
        updateRewardPoints,
        upgradeToProWithPoints,
        redeemPhysicalReward,
        checkProExpiry,
        proExpired,
        dismissProExpiry,
        setBusinessName,
        saveBusinessProfile,
        updateBusinessLogo,
        requestDriveAccess,
        settingsLoaded,
        error,
        readNotificationIds,
        updateNotificationReadIds,
        dismissedNotificationIds,
        dismissNotificationIds,
        hideFavoriteProducts,
        setHideFavoriteProducts,
        hideSalesSummary,
        setHideSalesSummary,
        printerPaperWidth,
        setPrinterPaperWidth,
        printerPrintLogo,
        setPrinterPrintLogo,
        deletionRequestedAt,
        deletionReason,
        requestAccountDeletion,
        revokeAccountDeletion,
        isProUpgradeModalOpen,
        openProUpgradeModal,
        closeProUpgradeModal,

        // Legacy-compat derived
        subscriptionId,
        subscriptionStatus,
        subscriptionPlan,
        subscriptionCurrentPeriodEnd,

        // Canonical
        plan,
        trialsUsed,
        devices,

        labelPrinterLanguage: labelPrinterLanguageState,
        setLabelPrinterLanguage,
        labelWidth: labelWidthState,
        labelHeight: labelHeightState,
        setLabelSize,
      }}
    >
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const context = useContext(DataContext);
  if (!context) throw new Error("useData must be used within a DataProvider");
  return context;
}